import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { BookOpen, FileText, Sparkles } from 'lucide-react';
import { DOCUMENT_CONTENT_QUERY, DOCUMENTS_QUERY } from '../graphql/queries';
import { FetchErrorCard } from '../components/FetchErrorCard';
import { JournalDocView } from '../components/DocumentViews';
import { Markdown } from '../components/Markdown';
import { useWikilinkResolver } from '../hooks/useWikilinkResolver';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';
import { displayBody, fileStem, friendlyDay, journalDayFromPath } from '../lib/docView';
import { parseJournalView } from '../lib/docView';

interface DocumentSummary {
  path: string;
  title: string;
  type: string | null;
  updatedAt: string;
  treeLabel: string;
  spaceId: string | null;
}

interface DocumentsData {
  documents: DocumentSummary[];
}

/** One calendar day on the timeline: every tree's document for that day. */
interface DayGroup {
  day: string;
  docs: DocumentSummary[];
}

/** How many days show at first; "Show earlier days" reveals another page. */
const PAGE_SIZE = 14;

/**
 * Entries page: a read-only journal timeline over the wiki's `journal/`
 * day documents (personal and shared trees together), newest day first. Each
 * calendar day appears ONCE (design review C2) — when the same day exists in
 * several trees, its moments render as sections under one day header, each
 * labeled with the tree it came from. Editing stays where it lives: each
 * section links to its document page.
 */
export function EntriesPage() {
  const { data, loading, error } = useQuery<DocumentsData>(DOCUMENTS_QUERY);
  const [limit, setLimit] = useState(PAGE_SIZE);

  const days = useMemo<DayGroup[]>(() => {
    const byDay = new Map<string, DocumentSummary[]>();
    for (const doc of data?.documents ?? []) {
      const day = journalDayFromPath(doc.path);
      if (!day) continue;
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day)!.push(doc);
    }
    const groups = [...byDay.entries()].map(([day, docs]) => ({
      day,
      // Personal first, then spaces alphabetically — a stable reading order.
      docs: docs.sort((a, b) => {
        if (a.treeLabel === 'Personal' && b.treeLabel !== 'Personal') return -1;
        if (b.treeLabel === 'Personal' && a.treeLabel !== 'Personal') return 1;
        return a.treeLabel.localeCompare(b.treeLabel) || a.path.localeCompare(b.path);
      }),
    }));
    groups.sort((a, b) => b.day.localeCompare(a.day));
    return groups;
  }, [data?.documents]);

  const visible = days.slice(0, limit);

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-24">
      <div className="px-1 pt-4 sm:pt-6 pb-2">
        <h1 className="text-2xl font-display font-semibold text-[var(--text-primary)]">Journal</h1>
        <p className="mt-1 text-[var(--text-secondary)]">Your days, newest first.</p>
      </div>

      {error && !data ? (
        <FetchErrorCard>
          Something went wrong loading your entries. Try again in a moment.
        </FetchErrorCard>
      ) : loading && !data ? (
        <div className="space-y-2.5" aria-hidden="true">
          <span className="skeleton block h-5 w-1/3 rounded-md" />
          <span className="skeleton block h-20 w-full rounded-xl" />
          <span className="skeleton block h-20 w-full rounded-xl" />
        </div>
      ) : days.length === 0 ? (
        <section className="glass-panel rounded-2xl p-8 border border-[var(--card-border)] shadow-soft text-center">
          <BookOpen
            className="w-8 h-8 mx-auto text-[var(--text-tertiary)]"
            aria-hidden
            strokeWidth={1.5}
          />
          <p className="mt-3 text-[var(--text-secondary)]">
            Nothing captured yet. Tell me about your day — a walk, a meal, a small win — and it
            shows up here.
          </p>
          <Link to="/alma" className="btn btn-primary mt-4 gap-2">
            <Sparkles className="w-4 h-4" aria-hidden /> Tell me
          </Link>
        </section>
      ) : (
        <>
          {visible.map((group) => (
            <DaySection key={group.day} group={group} />
          ))}
          {days.length > limit && (
            <div className="pt-2 flex justify-center">
              <button
                type="button"
                onClick={() => setLimit((prev) => prev + PAGE_SIZE)}
                className="btn btn-ghost"
              >
                Show earlier days ({visible.length} of {days.length})
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface DayContentData {
  documentContent: { path: string; content: string } | null;
}

/**
 * One calendar day on the timeline: a sticky day pill, then each tree's
 * moments. With a single (personal) document the moments render directly;
 * with several trees each gets a labeled section. Every section carries a
 * labeled link into its document. Display-only; nothing here writes.
 */
function DaySection({ group }: { group: DayGroup }) {
  const dateLabel = friendlyDay(group.day, 'long');
  const single = group.docs.length === 1 && !group.docs[0].spaceId;

  return (
    <section aria-label={dateLabel} data-testid="entries-day">
      <div className="sticky top-[calc(4rem+env(safe-area-inset-top))] z-10 mb-3 flex items-center gap-2">
        <span className="inline-block bg-[var(--surface)] px-3 py-1 text-sm font-semibold text-[var(--accent-ink)] border border-[var(--border)] rounded-full shadow-soft">
          {dateLabel}
        </span>
        {single && <OpenDayLink path={group.docs[0].path} dateLabel={dateLabel} />}
      </div>

      <div className="space-y-4">
        {group.docs.map((doc) => (
          <DayTreeSection key={doc.path} doc={doc} dateLabel={dateLabel} showHeader={!single} />
        ))}
      </div>
    </section>
  );
}

/** Quiet labeled link into the day's underlying document. */
function OpenDayLink({ path, dateLabel }: { path: string; dateLabel: string }) {
  return (
    <Link
      to={`/docs/${path}`}
      className={`inline-flex min-h-11 items-center gap-1.5 rounded-lg px-2 text-xs font-medium text-[var(--text-tertiary)] hover:text-[var(--accent-ink)] transition-colors shrink-0 ${FOCUS_RING_CLASSES}`}
      aria-label={`Open the document for ${dateLabel}`}
    >
      <FileText className="w-4 h-4" aria-hidden />
      Open day
    </Link>
  );
}

/** One tree's moments for the day, with an optional labeled section header. */
function DayTreeSection({
  doc,
  dateLabel,
  showHeader,
}: {
  doc: DocumentSummary;
  dateLabel: string;
  showHeader: boolean;
}) {
  const resolveWikilink = useWikilinkResolver();
  const { data, loading } = useQuery<DayContentData>(DOCUMENT_CONTENT_QUERY, {
    variables: { path: doc.path },
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  const content = data?.documentContent?.content ?? null;
  const view = content === null ? null : parseJournalView(doc.path, content);

  return (
    <div data-testid="entries-day-tree">
      {showHeader && (
        <div className="mb-2 flex items-center gap-2">
          <span className="text-xs font-medium text-[var(--accent-ink)] bg-[color-mix(in_oklab,var(--marine-400)_12%,transparent)] px-2 py-1 rounded-full">
            {doc.treeLabel}
          </span>
          <OpenDayLink path={doc.path} dateLabel={`${dateLabel} (${doc.treeLabel})`} />
        </div>
      )}

      {loading && !data ? (
        <span className="skeleton block h-20 w-full rounded-xl" aria-hidden="true" />
      ) : view ? (
        <JournalDocView view={view} />
      ) : content && content.trim().length > 0 ? (
        <article className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft">
          <Markdown
            content={displayBody(content, fileStem(doc.path))}
            className="font-reading text-sm sm:text-base"
            resolveWikilink={resolveWikilink}
          />
        </article>
      ) : (
        <p className="px-1 text-sm text-[var(--text-tertiary)]">Nothing captured this day.</p>
      )}
    </div>
  );
}
